import { useAtom } from "jotai"
import { atomWithStorage } from "jotai/utils"
import { useEffect } from "react"

import defaultCardsViewConfig, { CardsViewConfig, CardsViewConfigType } from "@constants/defaultCardsViewConfig"

const cardsViewConfigAtom = atomWithStorage<CardsViewConfigType>(
    "objectsCardsView",
    defaultCardsViewConfig,
    undefined,
    {
        getOnInit: true,
    },
)

function migrateCardsViewConfig(
    storedConfig: CardsViewConfigType,
    defaultConfig: CardsViewConfigType,
): CardsViewConfigType {
    const storedVersion = storedConfig.version ?? "1.0.0"
    const newVersion = defaultConfig.version ?? "1.0.0"

    const [storedMajor, storedMinor] = storedVersion.split(".")
    const [defaultMajor, defaultMinor] = newVersion.split(".")

    // If major version differs, reset the stored config (breaking changes)
    if (storedMajor !== defaultMajor) {
        return defaultConfig
    }

    const migratedConfig = structuredClone(storedConfig)
    migratedConfig.version = newVersion

    // If minor version differs, add the attributes the defaults gained and drop the ones they lost, keeping the
    // user's choice for every attribute that survived
    if (storedMinor !== defaultMinor) {
        Object.keys(migratedConfig.cards).forEach((key) => {
            const storedEntry = migratedConfig.cards[key]
            const defaultEntry = defaultConfig.cards[key]

            if (storedEntry && defaultEntry) {
                const attributeVisibility: CardsViewConfig["attributeVisibility"] = {}
                Object.keys(defaultEntry.attributeVisibility).forEach((attribute) => {
                    attributeVisibility[attribute] =
                        storedEntry.attributeVisibility[attribute] ?? defaultEntry.attributeVisibility[attribute]
                })
                storedEntry.attributeVisibility = attributeVisibility
            }
        })
    }

    return migratedConfig
}

export default function useCardsViewConfig(storageKey: string) {
    const [currentCardsViewConfig, setCardsViewConfig] = useAtom(cardsViewConfigAtom)

    const defaultConfig = defaultCardsViewConfig.cards[storageKey]
    const persistedConfig = currentCardsViewConfig.cards[storageKey]
    const effectiveConfig = persistedConfig ?? defaultConfig

    useEffect(() => {
        if (!currentCardsViewConfig || !defaultCardsViewConfig) {
            return
        }

        const hasChanges = currentCardsViewConfig.version !== defaultCardsViewConfig.version

        if (hasChanges) {
            setCardsViewConfig(migrateCardsViewConfig(currentCardsViewConfig, defaultCardsViewConfig))
        }
    }, [])

    const updateCardsViewConfig = (
        key: keyof CardsViewConfig,
        updatedValue: CardsViewConfig[keyof CardsViewConfig],
    ) => {
        const baseConfig = defaultCardsViewConfig.cards[storageKey]
        setCardsViewConfig((prevViews) => ({
            ...prevViews,
            cards: {
                ...prevViews.cards,
                [storageKey]: {
                    ...baseConfig,
                    ...(prevViews.cards[storageKey] as CardsViewConfig),
                    [key]: updatedValue,
                },
            },
        }))
    }

    // Read another list's entry as persisted, without this list's defaults.
    const getCardsViewConfig = (storageKey: string) => currentCardsViewConfig.cards[storageKey]

    return {
        currentCardsViewConfig: effectiveConfig,
        updateCardsViewConfig,
        getCardsViewConfig,
    }
}
